import type {
  FrameworkMetric,
  SecurityControlFrameworkMetrics,
} from "@app/deploy/security-controls";
import { IconExclamationCircle } from "@app/ui/shared/icons";
import cn from "classnames";
import { RadialProgress } from "./radial-progress";

const LOGO_BASE_PATH = "/assets/images/frameworks";

export interface FrameworkMetricsProps {
  metrics: SecurityControlFrameworkMetrics;
  selectedFrameworks: string[];
  isLoading: boolean;
  onToggle: (handle: string) => void;
  className?: string;
}

const FrameworkMetricCard = ({
  metric,
  isActive,
  onToggle,
  isLoading,
}: {
  metric: FrameworkMetric;
  isActive: boolean;
  onToggle: (handle: string) => void;
  isLoading: boolean;
}) => {
  const isLoadingState = isLoading;
  const isInteractive = !isLoadingState;

  return (
    <div
      className={cn(
        "relative flex flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm min-w-0",
        isInteractive && "cursor-pointer",
        isActive && "border-2 border-indigo-500 bg-indigo-50 shadow-md",
      )}
      role="button"
      tabIndex={isInteractive ? 0 : -1}
      aria-pressed={isActive}
      aria-disabled={!isInteractive}
      onClick={() => {
        if (isInteractive) {
          onToggle(metric.handle);
        }
      }}
      onKeyDown={(event) => {
        if (!isInteractive) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggle(metric.handle);
        }
      }}
      data-test-framework-card={metric.handle}
    >
      {/* Logo in top-right */}
      {metric.logo ? (
        <img
          src={`${LOGO_BASE_PATH}/${metric.logo}`}
          alt={metric.name}
          className="absolute right-3 top-3 h-9 w-9 object-contain opacity-90"
        />
      ) : null}

      {/* Circular Progress with centered percentage */}
      <div className="flex flex-col items-center mb-3 mt-1">
        <div className="relative h-[76px] w-[76px]">
          {/* Circular background */}
          <div
            className={cn(
              "absolute inset-1 rounded-full",
              isLoadingState
                ? "bg-gray-100"
                : metric.progressPercentage === 0
                  ? "bg-red-100"
                  : metric.progressPercentage === 100
                    ? "bg-emerald-100"
                    : "bg-amber-100",
            )}
          />
          {/* Radial progress graph */}
          <div className="absolute inset-0 flex items-center justify-center">
            {isLoadingState ? (
              <div className="h-12 w-12 animate-pulse rounded-full border-4 border-gray-200" />
            ) : metric.progressPercentage === 0 ? (
              // 0% - show exclamation mark
              <IconExclamationCircle color="#EF4444" />
            ) : (
              // 1-100% - show radial progress
              <RadialProgress
                progress={metric.progressPercentage}
                radius={36}
                strokeWidth={4}
                variant={
                  metric.progressPercentage === 100
                    ? "success"
                    : metric.progressPercentage === 0
                      ? "danger"
                      : "warn"
                }
              />
            )}
          </div>
          <span
            className={cn(
              "absolute inset-0 flex items-center justify-center font-normal text-base",
              isLoadingState && "text-gray-400",
              // Color matches the percentage value
              !isLoadingState &&
                metric.progressPercentage === 0 &&
                "text-red-500",
              metric.progressPercentage === 100 && "text-forest",
              metric.progressPercentage > 0 &&
                metric.progressPercentage < 100 &&
                "text-amber-700",
            )}
            style={
              isLoadingState
                ? undefined
                : metric.progressPercentage === 0
                  ? { color: "#EF4444" }
                  : metric.progressPercentage === 100
                    ? { color: "#00633F" }
                    : undefined
            }
            aria-hidden="true"
          >
            {isLoadingState ? "--" : `${metric.progressPercentage}%`}
          </span>
        </div>
      </div>

      {/* Title and Description */}
      <div className="text-center mb-3">
        <h3 className="text-sm font-semibold text-gray-900 mb-1 leading-tight">
          {metric.name}
        </h3>
        <p className="text-xs text-gray-600 leading-tight">
          {metric.description}
        </p>
      </div>

      {/* Metrics Table */}
      <div className="space-y-1 text-xs mb-3">
        <div className="flex justify-between gap-2">
          <span className="text-gray-700 truncate">Controls</span>
          <span className="font-medium text-gray-900 flex-shrink-0">
            {isLoadingState ? "--" : metric.controlCount}
          </span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-gray-700 truncate">Checks Performed</span>
          <span className="font-medium text-gray-900 flex-shrink-0">
            {isLoadingState ? "--" : metric.totalControlResourcesCount}
          </span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-gray-700 truncate">Checks Passed</span>
          <span className="font-medium text-gray-900 text-right flex-shrink-0">
            {isLoadingState
              ? "--"
              : `${metric.compliantControlResourcesCount} (${metric.progressPercentage}% of total)`}
          </span>
        </div>
      </div>
    </div>
  );
};

export const FrameworkMetrics = ({
  metrics,
  selectedFrameworks,
  isLoading,
  onToggle,
  className = "",
}: FrameworkMetricsProps) => {
  const ordered = Object.values(metrics).sort((a, b) => {
    const order = [
      "all",
      "hipaa-required",
      "hipaa-addressable",
      "hitrust-inheritable",
    ];
    const aIndex = order.indexOf(a.handle);
    const bIndex = order.indexOf(b.handle);
    return (
      (aIndex === -1 ? order.length : aIndex) -
      (bIndex === -1 ? order.length : bIndex)
    );
  });
  const loadingCards = Array.from({ length: 4 });

  return (
    <div
      className={cn(
        "w-full max-w-full min-w-0 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3",
        className,
      )}
    >
      {ordered.length === 0 && isLoading
        ? loadingCards.map((_, idx) => (
            <div
              key={`framework-loading-${idx}`}
              className="flex h-[260px] animate-pulse flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
            />
          ))
        : ordered.map((metric) => (
            <FrameworkMetricCard
              key={metric.handle}
              metric={metric}
              isActive={selectedFrameworks.includes(metric.handle)}
              onToggle={onToggle}
              isLoading={isLoading}
            />
          ))}
    </div>
  );
};
